import { Fragment, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { HowToVote } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { getEmptyToken } from "@dzambalaorg/mui-inputs-asset";
import { useUser } from "@dzambalaorg/provider-user";
import { useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { useApiCall } from "@dzambalaorg/react-hooks";
import { ListAction } from "@framework/styled";
import {
  AddressType,
  ContractFeatures,
  type IContract,
  type IUser,
  SystemModuleType,
  TokenType,
} from "@framework/types";
import ERC20ApproveABI from "@framework/abis/json/ERC20Simple/approve.json";
import ERC721SetApprovalABI from "@framework/abis/json/ERC721Simple/approve.json";
import ERC1155SetApprovalForAllABI from "@framework/abis/json/ERC1155Simple/setApprovalForAll.json";
import { Button } from "@dzambalaorg/ui-kit";

import { AllowanceDialog, IAllowanceDto } from "./dialog";

export interface IAllowanceButtonProps {
  token?: any;
  isSmall?: boolean;
  contract?: Partial<IContract>;
  isDisabled?: boolean;
  isExchange?: boolean;
}

export const AllowanceButton = (props: IAllowanceButtonProps) => {
  const { token = getEmptyToken(), isSmall = false, contract = undefined, isExchange = false } = props;

  const [exchange, setExchange] = useState<IContract | null>(null);

  const { chainId, isConnected } = useAccount();
  const { writeContractAsync } = useWriteContractWithSimulation();
  const user = useUser<IUser>();
  const isUserAuthenticated = user.isAuthenticated();

  // TODO useSystemContract
  const { fn: getContractExchangeFn } = useApiCall(
    api =>
      api.fetchJson({
        url: "/contracts/system",
        method: "POST",
        data: {
          contractModule: SystemModuleType.EXCHANGE,
        },
      }),
    { success: false, error: false },
  );

  const getSystemExchange = async () => {
    const exchange = await getContractExchangeFn();
    setExchange(exchange);
  };

  useEffect(() => {
    if (!contract && !exchange && isExchange && chainId) {
      void getSystemExchange();
    }
  }, [chainId]);

  const [isAllowanceDialogOpen, setIsAllowanceDialogOpen] = useState(false);
  const handleAllowance = (): void => {
    setIsAllowanceDialogOpen(true);
  };

  const handleAllowanceCancel = (): void => {
    setIsAllowanceDialogOpen(false);
  };

  const { execute: metaFn } = useMetamask(async (values: IAllowanceDto) => {
    const asset = values.token.components[0];

    if (asset.tokenType === TokenType.ERC20) {
      return writeContractAsync({
        address: asset.contract.address as AddressType,
        abi: ERC20ApproveABI,
        functionName: "approve",
        args: [values.address, asset.amount],
      });
    } else if (asset.tokenType === TokenType.ERC721 || asset.tokenType === TokenType.ERC998) {
      return writeContractAsync({
        address: asset.contract.address as AddressType,
        abi: ERC721SetApprovalABI,
        functionName: "approve",
        args: [values.address, asset.token.tokenId],
      });
    } else if (asset.tokenType === TokenType.ERC1155) {
      return writeContractAsync({
        address: asset.contract.address as AddressType,
        abi: ERC1155SetApprovalForAllABI,
        functionName: "setApprovalForAll",
        args: [values.address, true],
      });
    } else {
      throw new Error("unsupported token type");
    }
  });

  const handleAllowanceConfirm = async (values: IAllowanceDto): Promise<void> => {
    await metaFn(values);
  };

  const disabled = (contract || token?.template?.contract)?.contractFeatures.includes(ContractFeatures.SOULBOUND);

  if (!isConnected || !isUserAuthenticated) {
    return null;
  }

  return (
    <Fragment>
      {isSmall ? (
        <ListAction
          onClick={handleAllowance}
          icon={HowToVote}
          message="form.tips.allowance"
          disabled={disabled}
          dataTestId="StakeDepositAllowanceButton"
        />
      ) : (
        <Button
          disabled={disabled}
          intent="secondary"
          startIcon={<HowToVote />}
          onClick={handleAllowance}
          data-testid="AllowanceButton"
        >
          <FormattedMessage id="form.buttons.allowance" />
        </Button>
      )}

      <AllowanceDialog
        onCancel={handleAllowanceCancel}
        onConfirm={handleAllowanceConfirm}
        open={isAllowanceDialogOpen}
        initialValues={{
          token,
          address: contract?.address || exchange?.address || "",
          contractId: contract?.id || exchange?.id || undefined,
        }}
      />
    </Fragment>
  );
};
